import { Vector3 } from "@react-three/fiber";
import { useMemo } from "react";
import { useStoreRoom } from "../store";
import shallow from "zustand/shallow";
import { SideTypeWithoutTop } from "../store/types/store";
import { useShelvingStore } from "../store/useShelvingStore";
import {partsProperties} from "../shared";

export const usePosition = (side: SideTypeWithoutTop) => {
    const { roomLength, roomDepth, roomHeight } = useStoreRoom((state) => ({
        roomLength: state.length,
        roomDepth: state.depth,
        roomHeight: state.height,
    }), shallow);

    const { sideIndent, offsetCeiling, totalHeight, sections, beams } = useShelvingStore((state) => ({
        sideIndent: state.shelving[side].sideIndent,
        offsetCeiling: state.shelving[side].offsetCeiling,
        totalHeight: state.shelving[side].height,
        sections: state.shelving[side].sections,
        beams: state.shelving[side].beams,
    }), shallow);

    const startXPosition = useMemo(
        () => -((["left", "right"].includes(side) ? roomDepth : roomLength) / 2 - sideIndent) / 1000,
        [roomLength, roomDepth, sideIndent]
    );
    const startYPosition = useMemo(() => (roomHeight - offsetCeiling) / 1000, [roomHeight, offsetCeiling]);

    const firstRackPosition: Vector3 = useMemo(() => {
        return [startXPosition, startYPosition, 0];
    }, [startXPosition, startYPosition]);

    const leftRackInSectionPositions: Vector3[] = useMemo(() => {
        return sections.map((section) => [startXPosition + section.offset / 1000, startYPosition, 0]);
    }, [startXPosition, startYPosition, sections]);

    const rightRackInSectionPositions: Vector3[] = useMemo(() => {
        return sections.map((section) => [
            startXPosition + (section.offset + section.width - partsProperties.railing.vertical.doubleWidth) / 1000,
            startYPosition,
            0,
        ]);
    }, [startXPosition, startYPosition, sections]);

    const oneRackInSectionPositions: Vector3[] = useMemo(() => {
        return sections.map((section) => [
            startXPosition + (section.offset + section.width - partsProperties.railing.vertical.doubleWidth) / 1000,
            startYPosition,
            0,
        ]);
    }, [startXPosition, startYPosition, sections]);

    const dimensionPosition: Vector3[] = useMemo(() => {
        return sections.map((section) => [
            startXPosition + (section.offset + section.width) / 1000,
            startYPosition - totalHeight / 2000,
            0,
        ]);
    }, [startXPosition, startYPosition, sections]);

    const beamPosition: Vector3[] = useMemo(() => {
        return beams.map(beam => [
            startXPosition + beam.position[0] + beam.width / 2000,
            startYPosition + beam.position[1],
            beam.position[2]
        ])
    }, [startXPosition, startYPosition, beams]);

    const leftBracketShelfPosition: number[][][] = useMemo(() => {
        return sections.map((section) =>
            section.items.map((shelf) => [
                startXPosition + shelf.position[0] - section.width / 2000,
                shelf.position[1] - 0.031,
                0.015,
            ])
        );
    }, [startXPosition, startYPosition, sections]);

    const rightBracketShelfPosition: number[][][] = useMemo(() => {
        return sections.map((section) =>
            section.items.map((shelf) => [
                startXPosition + shelf.position[0] + section.width / 2000,
                shelf.position[1] - 0.031,
                0.015,
            ])
        );
    }, [startXPosition, startYPosition, sections]);

    const shelfPosition: number[][][] = useMemo(() => {
        return sections.map((section) =>
            section.items.map((shelf) => [
                startXPosition + shelf.position[0],
                shelf.position[1],
                shelf.type.some((t) => t === "Штанга") ? shelf.position[2] : shelf.depth / 2000 + 0.01,
            ])
        );
    }, [startXPosition, startYPosition, sections]);

    const partPosition: number[][][] = useMemo(() => {
        return sections.map((section) =>
            section.items.map((shelf) => [
                startXPosition + shelf.position[0],
                shelf.position[1],
                shelf?.depth / 2000 || 0.25,
            ])
        );
    }, [startXPosition, startYPosition, sections]);

    return {
        beamPosition,
        firstRackPosition,
        dimensionPosition,
        leftBracketShelfPosition,
        leftRackInSectionPositions,
        rightBracketShelfPosition,
        rightRackInSectionPositions,
        oneRackInSectionPositions,
        shelfPosition,
        partPosition,
        startXPosition,
    };
};
